import { useQueryClient } from '@tanstack/react-query'
import { lookupKey } from 'utils/constants/queryKey'
import { AppQueryOptions, useAppQueryMap } from 'hooks/use-app-query'
import { getFilterApi } from 'services/lookup'
import { FiltersRequestPayload, GetFiltersResponse } from 'services/types/lookup'
import { useAppSelector } from 'store'
import { STALE_TIME } from 'utils/constants'
import { LeadType } from 'types/global'
import { Filters } from 'store/types'

const useFilter = (payload?: FiltersRequestPayload, options?: AppQueryOptions) => {
  const branchId = useAppSelector((state) => state.user.selectedBranch?.branchId) ?? ''
  return useAppQueryMap([{ branchId }, ...lookupKey.filter(payload)], () => getFilterApi(payload), mapFilters, {
    staleTime: STALE_TIME.lookup,
    enabled: payload?.view ? true : false,
    ...options,
  })
}

const useInvalidateFilter = () => {
  const branchId = useAppSelector((state) => state.user.selectedBranch?.branchId) ?? ''
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries([{ branchId }, ...lookupKey.filters()])
}

export const mapFilters: (data: GetFiltersResponse) => Filters = (data) => {
  const { leads: leadFilterResponse, global_search: searchFilterResponse } = data

  const leads = {
    views: leadFilterResponse.views?.map(({ key, description }) => ({ key, description })) ?? [],
    leadTypes:
      leadFilterResponse.lead_types?.map(({ id, name, is_default }) => ({
        id: id as LeadType,
        name,
        isDefault: is_default,
      })) ?? [],
    leadOwners:
      leadFilterResponse.lead_owners?.map(({ id, name, fname, lname, type, branch_id, is_active, team_members }) => ({
        id,
        name,
        fname,
        lname,
        isActive: is_active,
        branchId: branch_id,
        type,
        teamMembers: team_members,
      })) ?? [],
    additionalOwners:
      leadFilterResponse.additional_owners?.map(({ id, name, fname, lname, branch_id, is_active }) => ({
        id,
        name,
        fname,
        lname,
        isActive: is_active,
        branchId: branch_id,
      })) ?? [],
    convertedBy:
      leadFilterResponse.converted_by?.map(({ id, name, fname, lname, branch_id, is_active }) => ({
        id,
        name,
        fname,
        lname,
        isActive: is_active,
        branchId: branch_id,
      })) ?? [],
    industries: leadFilterResponse.industries?.map(({ id, name }) => ({ id, name })) ?? [],
    leadStatuses: leadFilterResponse.lead_statuses?.map(({ id, name }) => ({ id, name })) ?? [],
    priorities: leadFilterResponse.priorities?.map(({ id, name }) => ({ id, name })) ?? [],
    leadCampaigns:
      leadFilterResponse.campaigns?.map((i) => ({
        leadCampaignId: i.campaign_id,
        leadCampaignName: i.campaign_name,
        leadSourceId: i.source_id,
      })) || [],
    leadSources: leadFilterResponse.sources?.map(({ id, name }) => ({ leadSourceId: id, leadSourceName: name })) || [],
    leadSourceTypes:
      leadFilterResponse.source_types?.map(({ id, name }) => ({ leadSourceTypeId: id, leadSourceTypeName: name })) ||
      [],
    closedReason:
      leadFilterResponse.closed_reason?.map(({ id, name, is_active }) => ({ id, name, isActive: is_active })) ?? [],
    closedBy:
      leadFilterResponse.closed_by?.map(({ id, name, fname, lname, branch_id, is_active }) => ({
        id,
        name,
        fname,
        lname,
        isActive: is_active,
        branchId: branch_id,
      })) ?? [],
    lastActivityOutcome: leadFilterResponse?.activity_outcomes?.map((outcome) => ({
      outcome: outcome?.outcome,
      outcomeId: outcome?.outcome_id,
    })),
    leadMarketPotential: leadFilterResponse?.market_potential ?? [],
    deletedBy:
      leadFilterResponse.deleted_by?.map(({ id, name, fname, lname, branch_id, is_active }) => ({
        id,
        name,
        fname,
        lname,
        isActive: is_active,
        branchId: branch_id,
      })) ?? [],
    totalCalls: leadFilterResponse?.total_calls ?? [],
  }

  const globalSearch = {
    leadTypes: searchFilterResponse.lead_type_id,
    fields: searchFilterResponse.fields,
  }

  return { leads, globalSearch }
}

export { useFilter, useInvalidateFilter }
